/**
 * Mounted Space calendar view
 *
 * Renders the active Space calendar surface used inside the dashboard,
 * including local task dates and the read-only external booking feed.
 *
 * Primary responsibilities:
 * - Render month, week, and day calendar layouts
 * - Forward task, event, date, and range actions to the dashboard
 *
 * Dependencies:
 * - Shared Space calendar feed types
 * - Space task update helpers
 */

"use client"

import { FC, useState, useMemo, useRef, useEffect } from "react"
import { Button } from "@/components/ui/button"
import {
  SpaceCalendarFeedEvent,
  SpaceTask,
  TaskPriority,
  TaskStatus
} from "@/types/spaces"
import { 
  IconChevronLeft,
  IconChevronRight,
  IconCalendar,
  IconClock,
  IconFlag,
  IconPlus,
  IconFilter
} from "@tabler/icons-react"
import { 
  format, 
  startOfMonth, 
  endOfMonth, 
  startOfWeek, 
  endOfWeek, 
  startOfDay,
  endOfDay,
  eachDayOfInterval,
  isSameMonth,
  isToday,
  addMonths,
  addDays,
  differenceInDays,
  parseISO,
  subDays,
  subMonths
} from "date-fns"

import { toast } from "sonner"
import { updateTask } from "@/db/space-tasks"
import { getPlanningDateSpanKeys } from "@/lib/spaces/planning-date-spans"

interface Task {
  id: string
  title: string
  status: TaskStatus
  priority: TaskPriority
  due_date?: string
  start_date?: string
  assigned_to?: string
  assigned_to_name?: string
  assigned_to_avatar?: string
  time_estimate_minutes?: number
  is_milestone?: boolean
  folder_id?: string
  sprint_id?: string
  tags?: string[]
}

type CalendarEvent = SpaceCalendarFeedEvent

interface CalendarViewProps {
  tasks: Task[]
  events?: CalendarEvent[]
  onTaskClick?: (task: Task) => void
  onEventClick?: (event: CalendarEvent) => void
  onDateClick?: (date: Date) => void
  onCreateTask?: (date: Date) => void
  onTaskUpdate?: (task: SpaceTask) => void
  onRangeChange?: (range: { start: string; end: string; view: 'month' | 'week' | 'day' }) => void
}

const getStatusColor = (status: string) => {
  switch (status) {
    case 'todo': return 'bg-gray-400 text-white'
    case 'in_progress': return 'bg-blue-500 text-white'
    case 'done': return 'bg-green-500 text-white'
    case 'blocked': return 'bg-red-500 text-white'
    case 'cancelled': return 'bg-gray-500 text-white'
    default: return 'bg-slate-500 text-white'
  }
}

const getPriorityColor = (priority: string) => {
  switch (priority) {
    case 'urgent': return 'border-red-500'
    case 'high': return 'border-orange-500'
    case 'medium': return 'border-yellow-500'
    case 'low': return 'border-green-500'
    default: return 'border-gray-300'
  }
}

const normalizeStatus = (status: string | null | undefined) => {
  if (!status) return "todo"
  return status.toLowerCase().replace(/[-\s]/g, "_")
}

const formatStatusLabel = (status: string) =>
  status
    .split("_")
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ")

const getEventColorClass = (event: CalendarEvent) => {
  switch (event.color) {
    case 'emerald':
      return 'bg-emerald-500 text-white'
    case 'sky':
      return 'bg-sky-500 text-white'
    case 'slate':
      return 'bg-slate-500 text-white'
    case 'amber':
      return 'bg-amber-500 text-white'
    default:
      return 'bg-purple-500 text-white'
  }
}

const getEventRange = (currentDate: Date, view: 'month' | 'week' | 'day') => {
  if (view === 'day') {
    return {
      start: startOfDay(currentDate),
      end: endOfDay(currentDate)
    }
  }

  if (view === 'week') {
    return {
      start: startOfWeek(currentDate),
      end: endOfWeek(currentDate)
    }
  }

  return {
    start: startOfWeek(startOfMonth(currentDate)),
    end: endOfWeek(endOfMonth(currentDate))
  }
}

const DATE_ONLY_EVENT_PATTERN = /^\d{4}-\d{2}-\d{2}$/
const DAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'] as const
const HOURS = Array.from({ length: 24 }, (_, hour) => hour)

const isDateOnlyEventValue = (value: string | undefined) =>
  typeof value === "string" && DATE_ONLY_EVENT_PATTERN.test(value.trim())

const parseEventDate = (
  value: string,
  options?: {
    endOfDayForDateOnly?: boolean
  }
) => {
  const trimmed = value.trim()

  if (isDateOnlyEventValue(trimmed)) {
    return parseISO(
      `${trimmed}T${options?.endOfDayForDateOnly ? "23:59:59" : "00:00:00"}`
    )
  }

  return new Date(trimmed)
}

const getEventEndDate = (event: CalendarEvent) => {
  const endValue = event.end_date || event.start_date

  if (
    event.all_day &&
    isDateOnlyEventValue(event.start_date) &&
    isDateOnlyEventValue(endValue) &&
    endValue !== event.start_date
  ) {
    return endOfDay(addDays(parseISO(endValue), -1))
  }

  return parseEventDate(endValue, {
    endOfDayForDateOnly: isDateOnlyEventValue(endValue)
  })
}

const getCurrentRangeLabel = (
  currentDate: Date,
  view: "month" | "week" | "day"
) => {
  if (view === "day") {
    return format(currentDate, "MMMM d, yyyy")
  }

  if (view === "week") {
    const start = startOfWeek(currentDate)
    const end = endOfWeek(currentDate)

    if (format(start, "yyyy-MM") === format(end, "yyyy-MM")) {
      return `${format(start, "MMMM d")} - ${format(end, "d, yyyy")}`
    }

    return `${format(start, "MMM d")} - ${format(end, "MMM d, yyyy")}`
  }

  return format(currentDate, "MMMM yyyy")
}

export const CalendarView: FC<CalendarViewProps> = ({
  tasks,
  events = [],
  onTaskClick,
  onEventClick,
  onDateClick,
  onCreateTask,
  onTaskUpdate,
  onRangeChange
}) => {
  const [currentDate, setCurrentDate] = useState(new Date())
  const [view, setView] = useState<'month' | 'week' | 'day'>('month')
  const [showFilters, setShowFilters] = useState(false)
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>([])
  const isDraggingRef = useRef(false)
  const onRangeChangeRef = useRef(onRangeChange)

  useEffect(() => {
    onRangeChangeRef.current = onRangeChange
  }, [onRangeChange])

  const availableStatuses = useMemo(() => {
    const statuses = Array.from(
      new Set(tasks.map(task => normalizeStatus(task.status)))
    )

    if (statuses.length === 0) {
      return ["todo", "in_progress", "done", "blocked", "cancelled"]
    }

    return statuses
  }, [tasks])

  useEffect(() => {
    setSelectedStatuses(currentSelected => {
      if (currentSelected.length === 0) {
        return availableStatuses
      }

      const retainedSelections = currentSelected.filter(status =>
        availableStatuses.includes(status)
      )

      return retainedSelections.length > 0
        ? retainedSelections
        : availableStatuses
    })
  }, [availableStatuses])

  const selectedStatusSet = useMemo(
    () => new Set(selectedStatuses),
    [selectedStatuses]
  )

  useEffect(() => {
    const range = getEventRange(currentDate, view)
    onRangeChangeRef.current?.({
      start: range.start.toISOString(),
      end: range.end.toISOString(),
      view
    })
  }, [currentDate, view])

  // Generate calendar days
  const calendarDays = useMemo(() => {
    const monthStart = startOfMonth(currentDate)
    const monthEnd = endOfMonth(currentDate)
    const calendarStart = startOfWeek(monthStart)
    const calendarEnd = endOfWeek(monthEnd)
    
    return eachDayOfInterval({ start: calendarStart, end: calendarEnd })
  }, [currentDate])

  // Group tasks and events by date
  const itemsByDate = useMemo(() => {
    const grouped: Record<string, { tasks: Task[]; events: CalendarEvent[] }> = {}
    
    // Group tasks
    tasks
      .filter(task => selectedStatusSet.has(normalizeStatus(task.status)))
      .forEach(task => {
        getPlanningDateSpanKeys(task.start_date, task.due_date).forEach(dateKey => {
          if (!grouped[dateKey]) grouped[dateKey] = { tasks: [], events: [] }
          grouped[dateKey].tasks.push(task)
        })
      })
    
    // Group events
    events.forEach(event => {
      const eventStart = parseEventDate(event.start_date)
      const eventEnd = getEventEndDate(event)
      const eventDays = eachDayOfInterval({
        start: startOfDay(eventStart),
        end: startOfDay(eventEnd)
      })

      eventDays.forEach((day) => {
        const dateKey = format(day, 'yyyy-MM-dd')
        if (!grouped[dateKey]) grouped[dateKey] = { tasks: [], events: [] }
        grouped[dateKey].events.push(event)
      })
    })
    
    return grouped
  }, [tasks, events, selectedStatusSet])

  const currentRangeLabel = useMemo(
    () => getCurrentRangeLabel(currentDate, view),
    [currentDate, view]
  )

  const weekDays = useMemo(() => {
    const weekStart = startOfWeek(currentDate)

    return DAY_LABELS.map((_, dayIndex) => addDays(weekStart, dayIndex))
  }, [currentDate])

  const currentDateKey = useMemo(
    () => format(currentDate, 'yyyy-MM-dd'),
    [currentDate]
  )

  const navigateRange = (direction: "prev" | "next") => {
    setCurrentDate((currentValue) => {
      if (view === "day") {
        return direction === "prev"
          ? subDays(currentValue, 1)
          : addDays(currentValue, 1)
      }

      if (view === "week") {
        return direction === "prev"
          ? subDays(currentValue, 7)
          : addDays(currentValue, 7)
      }

      return direction === "prev"
        ? subMonths(currentValue, 1)
        : addMonths(currentValue, 1)
    })
  }

  const TaskItem: FC<{ task: Task }> = ({ task }) => (
    <div
      className={`mb-1 cursor-pointer rounded border p-1 text-xs transition-all hover:shadow-sm ${
        getStatusColor(task.status)
      } ${getPriorityColor(task.priority || 'medium')}`}
      onClick={(e) => {
        e.stopPropagation()
        if (isDraggingRef.current) return
        onTaskClick?.(task)
      }}
      title={`${task.title} - ${formatStatusLabel(normalizeStatus(task.status))} (${task.priority})`}
      draggable
      onDragStart={(e) => {
        isDraggingRef.current = true
        e.dataTransfer.setData(
          "text/plain",
          JSON.stringify({ taskId: task.id })
        )
        e.dataTransfer.effectAllowed = "move"
      }}
      onDragEnd={() => {
        isDraggingRef.current = false
      }}
    >
      <div className="flex items-center gap-1 break-words font-medium">
        {task.is_milestone && <IconFlag size={10} />}
        {task.title}
      </div>
      {task.assigned_to && (
        <div className="mt-1 flex items-center gap-1">
          <div className="relative inline-flex size-5 items-center justify-center overflow-hidden rounded-full border">
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              src={task.assigned_to_avatar || ''}
              alt="assignee"
              className="size-full object-cover"
              onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none' }}
            />
            <span className="bg-muted absolute inset-0 flex items-center justify-center text-[8px] font-medium">
              {(task.assigned_to_name || 'U')
                .toString()
                .charAt(0)
                .toUpperCase()}
            </span>
          </div>
        </div>
      )}
    </div>
  )

  const EventItem: FC<{ event: CalendarEvent }> = ({ event }) => (
    <div
      className={`mb-1 cursor-pointer rounded p-1 text-xs transition-all hover:shadow-sm ${getEventColorClass(event)}`}
      onClick={(e) => {
        e.stopPropagation()
        onEventClick?.(event)
      }}
      title={event.description || event.title}
    >
      <div className="flex items-center gap-1 truncate font-medium">
        <IconCalendar size={10} />
        {event.title}
      </div>
      {event.source_label && (
        <div className="mt-1 truncate text-[10px] opacity-80">
          {event.source_label}
          {event.resource_names?.length ? ` • ${event.resource_names.join(", ")}` : ""}
          {event.read_only ? " • Read only" : ""}
        </div>
      )}
      {!event.all_day && (
        <div className="mt-1 flex items-center gap-1 opacity-75">
          <IconClock size={8} />
          <span>{format(parseEventDate(event.start_date), 'HH:mm')}</span>
        </div>
      )}
    </div>
  )

  const CalendarDay: FC<{ date: Date }> = ({ date }) => {
    const dateKey = format(date, 'yyyy-MM-dd')
    const dayItems = itemsByDate[dateKey] || { tasks: [], events: [] }
    const visibleEvents = dayItems.events.slice(0, 3)
    const remainingTaskSlots = Math.max(0, 3 - visibleEvents.length)
    const visibleTasks = dayItems.tasks.slice(0, remainingTaskSlots)
    const hiddenCount =
      dayItems.events.length +
      dayItems.tasks.length -
      visibleEvents.length -
      visibleTasks.length
    const isCurrentMonth = isSameMonth(date, currentDate)
    const isCurrentDay = isToday(date)

    const handleTaskDrop = async (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault()

      const raw = e.dataTransfer.getData("text/plain")
      if (!raw) return

      let taskId: string
      try {
        const parsed = JSON.parse(raw) as { taskId: string }
        taskId = parsed.taskId
      } catch {
        return
      }

      const task = tasks.find(t => t.id === taskId)
      if (!task) return
      if (!task.due_date) return

      const oldDue = new Date(task.due_date)
      const newDue = parseISO(dateKey)
      const deltaDays = differenceInDays(newDue, oldDue)

      const updates: { due_date: string; start_date?: string; space_id: string } = {
        space_id: task.space_id,
        due_date: newDue.toISOString()
      }

      if (task.start_date) {
        const oldStart = new Date(task.start_date)
        const newStart = addDays(oldStart, deltaDays)
        updates.start_date = newStart.toISOString()
      }

      try {
        const updated = await updateTask(task.id, updates)
        onTaskUpdate?.(updated as SpaceTask)
        toast.success("Task date updated")
      } catch {
        toast.error("Failed to update task date")
      } finally {
        isDraggingRef.current = false
      }
    }
    
    return (
      <div
        className={`border-muted group min-h-28 touch-manipulation border p-1.5 transition-colors sm:min-h-32 sm:p-2 ${
          onDateClick ? "hover:bg-muted/50 cursor-pointer" : ""
        } ${!isCurrentMonth ? 'bg-muted/20 text-muted-foreground' : ''} ${
          isCurrentDay ? 'border-blue-200 bg-blue-50' : ''
        }`}
        onClick={() => onDateClick?.(date)}
        onDragOver={e => e.preventDefault()}
        onDrop={handleTaskDrop}
      >
        {/* Day Number */}
        <div className="mb-2 flex items-center justify-between">
          <span className={`text-sm font-medium ${isCurrentDay ? 'text-blue-600' : ''}`}>
            {format(date, 'd')}
          </span>
          {isCurrentMonth && onCreateTask && (
            <Button
              variant="ghost"
              size="sm"
              className="size-9 touch-manipulation p-0 opacity-100 sm:size-4 sm:opacity-0 sm:group-hover:opacity-100"
              onClick={(e) => {
                e.stopPropagation()
                onCreateTask?.(date)
              }}
            >
              <IconPlus size={10} />
            </Button>
          )}
        </div>

        {/* Events */}
        {visibleEvents.map((event) => (
          <EventItem key={event.id} event={event} />
        ))}

        {/* Tasks */}
        {visibleTasks.map((task) => (
          <TaskItem key={task.id} task={task} />
        ))}

        {/* More Items Indicator */}
        {hiddenCount > 0 && (
          <div className="text-muted-foreground mt-1 text-xs">
            +{hiddenCount} more
          </div>
        )}
      </div>
    )
  }

  return (
    <div className="calendar-view flex h-full min-w-0 flex-col overflow-hidden">
      {/* Header */}
      <div className="flex flex-col gap-3 border-b p-3 lg:flex-row lg:items-center lg:justify-between lg:p-4">
        <div className="flex flex-wrap items-center gap-2 sm:gap-4">
          <h2 className="text-lg font-semibold">Calendar</h2>
          
          {/* Month Navigation */}
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" className="min-h-10 touch-manipulation sm:min-h-9" onClick={() => navigateRange('prev')}>
              <IconChevronLeft size={16} />
            </Button>
            <span className="min-w-28 text-center text-sm font-medium sm:min-w-32 sm:text-base">
              {currentRangeLabel}
            </span>
            <Button variant="outline" size="sm" className="min-h-10 touch-manipulation sm:min-h-9" onClick={() => navigateRange('next')}>
              <IconChevronRight size={16} />
            </Button>
          </div>

          {/* Today Button */}
          <Button
            variant="outline"
            size="sm"
            className="min-h-10 touch-manipulation sm:min-h-9"
            onClick={() => setCurrentDate(new Date())}
          >
            Today
          </Button>
        </div>

        <div className="flex touch-pan-x items-center gap-2 overflow-x-auto lg:overflow-visible">
          {/* View Toggle */}
          <div className="flex items-center rounded-lg border">
            {(['month', 'week', 'day'] as const).map((viewType) => (
              <Button
                key={viewType}
                variant={view === viewType ? "default" : "ghost"}
                size="sm"
                className="min-h-10 shrink-0 touch-manipulation rounded-none first:rounded-l-lg last:rounded-r-lg sm:min-h-9"
                onClick={() => setView(viewType)}
              >
                {viewType.charAt(0).toUpperCase() + viewType.slice(1)}
              </Button>
            ))}
          </div>

          {/* Filters */}
          <Button
            variant="outline"
            size="sm"
            className="min-h-10 shrink-0 touch-manipulation sm:min-h-9"
            onClick={() => setShowFilters(!showFilters)}
          >
            <IconFilter size={16} className="mr-2" />
            Filter
          </Button>
        </div>
      </div>

      {/* Filters Panel */}
      {showFilters && (
        <div className="bg-muted/30 border-b p-3 sm:p-4">
          <div className="flex flex-wrap items-center gap-3 sm:gap-4">
            <span className="text-sm font-medium">Show tasks:</span>
            {availableStatuses.map((status) => (
              <label key={status} className="flex min-h-10 touch-manipulation items-center gap-2 text-sm sm:min-h-0">
                <input
                  type="checkbox"
                  checked={selectedStatuses.includes(status)}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setSelectedStatuses([...selectedStatuses, status])
                    } else {
                      setSelectedStatuses(selectedStatuses.filter(s => s !== status))
                    }
                  }}
                />
                <span>{formatStatusLabel(status)}</span>
              </label>
            ))}
          </div>
        </div>
      )}

      {/* Calendar Grid */}
      {view === 'month' && (
        <div className="min-h-0 flex-1 touch-pan-x overflow-x-auto overscroll-x-contain">
          <div className="min-w-[720px]">
          {/* Day Headers */}
          <div className="grid grid-cols-7 border-b">
            {DAY_LABELS.map((day) => (
              <div key={day} className="bg-muted/50 p-2 text-center text-sm font-medium">
                {day}
              </div>
            ))}
          </div>

          {/* Calendar Days */}
          <div className="group grid flex-1 grid-cols-7 overflow-y-auto">
            {calendarDays.map((date) => (
              <CalendarDay key={date.toISOString()} date={date} />
            ))}
          </div>
          </div>
        </div>
      )}

      {/* Week View */}
      {view === 'week' && (
        <div className="min-h-0 flex-1 touch-pan-x overflow-auto overscroll-contain">
          <div className="flex h-full min-w-[860px]">
            {/* Time column */}
            <div className="w-16 border-r">
              <div className="bg-background sticky top-0 z-20 h-12 border-b" />
              {HOURS.map((i) => (
                <div key={i} className="text-muted-foreground h-12 border-b p-1 text-center text-xs">
                  {i.toString().padStart(2, '0')}:00
                </div>
              ))}
            </div>
            
            {/* Week days */}
            <div className="grid flex-1 grid-cols-7">
              {weekDays.map((currentDay, dayIndex) => {
                const dateKey = format(currentDay, 'yyyy-MM-dd')
                const dayItems = itemsByDate[dateKey] || { tasks: [], events: [] }
                
                return (
                  <div key={dayIndex} className="border-r">
                    {/* Day header */}
                    <div className="bg-background sticky top-0 z-10 h-12 border-b p-2 text-center">
                      <div className="text-sm font-medium">{format(currentDay, 'EEE')}</div>
                      <div className={`text-lg ${isToday(currentDay) ? 'font-bold text-blue-600' : ''}`}>
                        {format(currentDay, 'd')}
                      </div>
                    </div>
                    
                    {/* Day content */}
                    <div className="relative">
                      {/* Hour slots */}
                      {HOURS.map((hour) => (
                        <div key={hour} className="h-12 border-b border-gray-100" />
                      ))}
                      
                      {/* Events and tasks overlay */}
                      <div className="absolute inset-0 p-1">
                        {dayItems.events.map((event, index) => {
                          const eventStart = parseEventDate(event.start_date)

                          return (
                            <div
                              key={event.id}
                              className={`absolute inset-x-1 z-10 cursor-pointer rounded p-1 text-xs ${getEventColorClass(event)}`}
                              style={{
                                top: event.all_day
                                  ? `${index * 20}px`
                                  : `${eventStart.getHours() * 48 + eventStart.getMinutes() * 0.8}px`,
                                height: event.all_day ? '18px' : '40px'
                              }}
                              onClick={() => onEventClick?.(event)}
                            >
                              <div className="truncate font-medium">{event.title}</div>
                              {event.source_label && (
                                <div className="truncate text-[10px] opacity-80">
                                  {event.source_label}
                                </div>
                              )}
                            </div>
                          )
                        })}
                        
                        {dayItems.tasks.map((task, index) => (
                          <div
                            key={task.id}
                            className={`absolute inset-x-1 cursor-pointer rounded border p-1 text-xs ${
                              getStatusColor(task.status)
                            } ${getPriorityColor(task.priority || 'medium')}`}
                            style={{
                              top: `${(dayItems.events.length * 20) + (index * 22)}px`,
                              height: '20px'
                            }}
                            onClick={() => onTaskClick?.(task)}
                          >
                            <div className="truncate font-medium">{task.title}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        </div>
      )}

      {/* Day View */}
      {view === 'day' && (
        <div className="min-h-0 flex-1 touch-pan-x overflow-auto overscroll-contain">
          <div className="flex h-full min-w-[720px]">
            {/* Time column */}
            <div className="w-20 border-r">
              <div className="bg-background sticky top-0 z-20 flex h-16 items-center justify-center border-b">
                <span className="text-sm font-medium">Time</span>
              </div>
              {HOURS.map((i) => (
                <div key={i} className="text-muted-foreground h-16 border-b p-2 text-center text-sm">
                  <div>{i.toString().padStart(2, '0')}:00</div>
                  <div className="mt-1 text-xs">{i < 12 ? 'AM' : 'PM'}</div>
                </div>
              ))}
            </div>
            
            {/* Day content */}
            <div className="flex-1">
              {/* Day header */}
              <div className="h-16 border-b p-4">
                <div className="text-lg font-medium">{format(currentDate, 'EEEE, MMMM d, yyyy')}</div>
                <div className="text-muted-foreground text-sm">
                  {isToday(currentDate) && <span className="font-medium text-blue-600">Today</span>}
                </div>
              </div>
              
              {/* Day timeline */}
              <div className="relative">
                {/* Hour slots */}
                {HOURS.map((hour) => (
                  <div key={hour} className="h-16 cursor-pointer border-b border-gray-100 hover:bg-gray-50">
                    <div className="h-8 border-b border-gray-50"></div>
                  </div>
                ))}
                
                {/* Current time indicator */}
                {isToday(currentDate) && (
                  <div
                    className="absolute inset-x-0 z-20 border-t-2 border-red-500"
                    style={{
                      top: `${new Date().getHours() * 64 + new Date().getMinutes() * 1.067}px`
                    }}
                  >
                    <div className="-mt-1 size-2 rounded-full bg-red-500"></div>
                  </div>
                )}
                
                {/* Events and tasks overlay */}
                <div className="absolute inset-0 p-2">
                  {(() => {
                    const dayItems = itemsByDate[currentDateKey] || { tasks: [], events: [] }
                    
                    return (
                      <>
                        {dayItems.events.map((event) => {
                          const eventStart = parseEventDate(event.start_date)
                          const eventEnd = getEventEndDate(event)

                          return (
                            <div
                              key={event.id}
                              className={`absolute inset-x-2 cursor-pointer rounded p-2 shadow-sm ${getEventColorClass(event)}`}
                              style={{
                                top: event.all_day
                                  ? '0px'
                                  : `${eventStart.getHours() * 64 + eventStart.getMinutes() * 1.067}px`,
                                height: event.all_day
                                  ? '30px'
                                  : `${(eventEnd.getTime() - eventStart.getTime()) / (1000 * 60) * 1.067}px`,
                                zIndex: 10
                              }}
                              onClick={() => onEventClick?.(event)}
                            >
                              <div className="font-medium">{event.title}</div>
                              {event.source_label && (
                                <div className="mt-1 text-[10px] opacity-80">
                                  {event.source_label}
                                  {event.read_only ? " • Read only" : ""}
                                </div>
                              )}
                              {event.description && (
                                <div className="mt-1 text-xs opacity-90">{event.description}</div>
                              )}
                            </div>
                          )
                        })}
                        
                        {dayItems.tasks.map((task, index) => (
                          <div
                            key={task.id}
                            className={`absolute right-2 w-1/3 cursor-pointer rounded border p-2 ${
                              getStatusColor(task.status)
                            } ${getPriorityColor(task.priority || 'medium')}`}
                            style={{
                              top: task.due_date 
                                ? `${new Date(task.due_date).getHours() * 64 + new Date(task.due_date).getMinutes() * 1.067}px`
                                : `${index * 40}px`,
                              height: '35px'
                            }}
                            onClick={() => onTaskClick?.(task)}
                          >
                            <div className="break-words text-sm font-medium">{task.title}</div>
                            {task.assigned_to && (
                              <div className="flex items-center">
                                <div className="relative inline-flex size-4 items-center justify-center overflow-hidden rounded-full border">
                                  {/* eslint-disable-next-line @next/next/no-img-element */}
                                  <img
                                    src={task.assigned_to_avatar || ''}
                                    alt="assignee"
                                    className="size-full object-cover"
                                    onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none' }}
                                  />
                                  <span className="bg-muted absolute inset-0 flex items-center justify-center text-[7px] font-medium">
                                    {(task.assigned_to_name || 'U')
                                      .toString()
                                      .charAt(0)
                                      .toUpperCase()}
                                  </span>
                                </div>
                              </div>
                            )}
                          </div>
                        ))}
                      </>
                    )
                  })()}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Legend */}
      <div className="bg-muted/30 border-t p-4">
        <div className="flex items-center gap-6 text-xs">
          <div className="flex items-center gap-2">
            <div className="size-3 rounded bg-blue-500" />
            <span>In Progress</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="size-3 rounded bg-green-500" />
            <span>Completed</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="size-3 rounded bg-red-500" />
            <span>Blocked</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="size-3 rounded bg-emerald-500" />
            <span>Space events</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="size-3 rounded bg-sky-500" />
            <span>Google Calendar</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="size-3 rounded bg-slate-500" />
            <span>Apple Calendar</span>
          </div>
          <div className="flex items-center gap-2">
            <IconFlag size={12} className="text-yellow-500" />
            <span>Milestone</span>
          </div>
        </div>
      </div>
    </div>
  )
}
